<script setup lang="ts">
import Filter from '@/assets/icon-filter.svg?component'

import groupBy from 'lodash/groupBy'
import filter from 'lodash/filter'
import reduce from 'lodash/reduce'
import keyBy from 'lodash/keyBy'
import type { Dictionary } from 'lodash'
import type { WritableComputedRef } from '@vue/reactivity'

interface Event {
  contractor: string;
  date: string;
  day_of_month: string;
  day_of_week: string;
  id: string;
  month: string;
  options: string;
  options_formated: string;
  track: string;
}

type Events = Array<Event>

interface Contractor {
  name: string;
  url: string;
}

type Contractors = Array<Contractor>

interface Option {
  number: number;
  content: string;
}

type Options = Array<Option>

interface Track {
  name: string;
  length: string;
  address: string;
}

type Tracks = Array<Track>

const { data: calendar }: { data: Ref<{
    events: Events;
    contractors: Contractors;
    tracks: Dictionary<Track>;
    options: Record<string, string>;
}> } = await useAsyncData('calendar', async () => {
  return await Promise.all([
    $fetch<Events>('/calendar-api/events'),
    $fetch<Contractors>('/calendar-api/contractors'),
    $fetch<Tracks>('/calendar-api/tracks'),
    $fetch<Options>('/calendar-api/options')
  ])
}, {
  transform ([events, contractors, tracks, options]) {
    return { 
      events,
      contractors,
      tracks: keyBy(tracks, 'name'),
      options: options.reduce<Record<Option['number'], Option['content']>>((acc, { number, content }) => ({ ...acc, [number]: content }), {}) 
    }
  }
})

const { baseUrl } = useRuntimeConfig().public

const route = useRoute()
const { path } = route
const query = toRef(route, 'query')

const contractors = computed(() => calendar.value.contractors)
const tracks = computed(() => calendar.value.tracks)
const options = computed(() => calendar.value.options)

const createFilter = (name: string) => computed({
  get () {
    return Array.isArray(query.value[name]) ? query.value[name] : [query.value[name]].filter(Boolean)
  },
  set (filters) {
    navigateTo({ query: { ...route.query, [name]: filters } })
  }
})

const createFilters = <T extends keyof Event>(filterNames: Array<T> | Set<T>) => 
  reactive([...new Set(filterNames)].reduce((filters, filterName) => {
    filters[filterName] = createFilter(filterName)
    return filters
  }, {} as Record<T, WritableComputedRef<LocationQueryValue[]>>))


const filters = createFilters(['track', 'contractor'])

const hasFilters = computed(() => reduce(filters, (acc, filter) => acc || Boolean(toValue(filter).length), false))

const filteredEvents = computed(() => {
  return hasFilters
    ? filter(calendar.value.events, event =>
      reduce(filters, (acc: boolean, filterValues, filter: keyof Event) =>
        acc && (Boolean(toValue(filterValues).length) ? toValue(filterValues).includes(event[filter]) : true)
      , true)
    )
    : calendar.value.events
})

const hasEvents = computed(() => filteredEvents.value.length)

const eventsByMonth = computed(() => groupBy(filteredEvents.value, 'month'))

const handleReset = () => navigateTo({ query: Object.assign({}, query.value, Object.keys(filters).reduce((acc, filterName) => ({ ...acc, [filterName]: []}), {})) })

const getOption = (option: string) => options.value[option]

const filtersDialogOpen = ref(false)
const showFiltersButton = ref<HTMLElement | null>(null)

useMatchMedia('(min-width: 768px)', (e) => {
  e.matches && (filtersDialogOpen.value = false)
})

useHead({
  titleTemplate: 'Kalendarz jazd - %s',
  meta: [
    { name: 'robots', content: 'index,follow' }
  ],
  link: [{ hid: 'canonical', rel: 'canonical', href: `${baseUrl}${path}` }]
})

const onEnter = (element: any) => {
  element.style.height = 'auto'

  const height = getComputedStyle(element).height

  element.style.position = ''
  element.style.visibility = ''
  element.style.height = '0'

  // eslint-disable-next-line no-unused-expressions
  getComputedStyle(element).height

  requestAnimationFrame(() => {
    element.style.height = height
  })
}

const onAfterEnter = (element: any) => {
  element.style.height = 'auto'
}

const onLeave = (element: any) => {
  const height = getComputedStyle(element).height

  element.style.height = height

  // eslint-disable-next-line no-unused-expressions
  getComputedStyle(element).height

  requestAnimationFrame(() => {
    element.style.height = '0'
  })
}
</script>

<template>
  <div class="container mx-auto">
    <Breadcrumbs :breadcrumbs="[{ name: 'Kalendarz jazd' }]" />
    <h1 class="text-24 md:text-32 font-bold mt-[30px]">
      Kalendarz jazd
    </h1>
    <div class="flex flex-col md:flex-row gap-[5%] mt-[20px]">
      <div class="w-full md:w-1/5 hidden md:block">
        <div class="flex justify-between items-center">
          <span class="inline-flex items-center gap-[5px] text-16 font-bold">
            <Filter />
            Filtry
          </span>
          <button v-if="hasFilters" @click="handleReset">
            Wyczyść filtry
          </button>
        </div>

        <Disclosure class="border-b border-gray-400 mt-[20px]">
          <Disclosure.Button class="group w-full text-left flex items-center justify-between py-[8px] pr-[4px] text-16 font-medium">
            Tor <i class="icon-arrow-button text-[15px] transition-transform rotate-90 group-aria-expanded:-rotate-90" />
          </Disclosure.Button>
          <Disclosure.Panel #default="{ isOpen }">
            <Transition.Panel>
              <div v-show="isOpen">
                <div class="flex flex-col pb-[15px]">
                  <label v-for="track in tracks" :key="track.name" class="relative inline-flex items-baseline cursor-pointer py-[5px]">
                    <input v-model="filters.track" name="tracks" type="checkbox" :value="track.name" class="shrink-0 w-[14px] h-[14px] accent-action">
                    <span class="ml-[5px]">{{ track.name }}</span>
                  </label>
                </div>
              </div>
            </Transition.Panel>
          </Disclosure.Panel>
        </Disclosure>

        <Disclosure class="border-b border-gray-400">
          <Disclosure.Button class="group w-full text-left flex items-center justify-between py-[8px] pr-[4px] text-16 font-medium">
            Wykonawcy <i class="icon-arrow-button text-[15px] transition-transform rotate-90 group-aria-expanded:-rotate-90" />
          </Disclosure.Button>
          <Disclosure.Panel #default="{ isOpen }">
            <Transition.Panel>
              <div v-show="isOpen">
                <div class="flex flex-col pb-[15px]">
                  <label v-for="contractor in contractors" :key="contractor.name" class="relative inline-flex items-baseline cursor-pointer py-[5px]">
                    <input v-model="filters.contractor" name="contractor" type="checkbox" :value="contractor.name" class="shrink-0 w-[14px] h-[14px] accent-action">
                    <span class="ml-[5px]">{{ contractor.name }}</span>
                  </label>
                </div>
              </div>
            </Transition.Panel>
          </Disclosure.Panel>
        </Disclosure>
      </div>

      <div class="flex md:hidden justify-between items-center mb-[10px]">
        <button ref="showFiltersButton" class="inline-flex items-center gap-[5px] text-16 font-bold" @click="filtersDialogOpen = true">
          <Filter />
          Filtry
        </button>
        <button v-if="hasFilters" @click="handleReset">
          Wyczyść filtry
        </button>
      </div>

      <div class="w-full md:w-3/4 flex flex-col gap-[2px]">
        <div class="flex gap-[2px] text-white min-h-[50px] text-16">
          <div class="w-[30px] bg-gray-900" />
          <div class="flex flex-1 justify-center items-center border border-gray-900 bg-gray-900">
            Data
          </div>
          <div class="flex flex-1 justify-center items-center border border-gray-900 bg-gray-900">
            Tor
          </div>
          <div class="flex flex-1 justify-center items-center border border-gray-900 bg-gray-900">
            Wykonawca
          </div>
        </div>
        <div class="relative z-0">
          <div v-if="!hasEvents" class="text-center">
            <p class="text-16 font-medium mt-[20px]">
              Niestety nie znaleźliśmy dostępnych terminów.
            </p>
            <p v-if="hasFilters" class="mt-[10px]">
              Spróbuj zresetować zastosowane filtry.
              <button class="text-red" @click="handleReset">
                Wyczyść filtry
              </button>
            </p>
          </div>
          <Disclosure.Group v-else accordion>
            <TransitionGroup
              name="panel-group"
              tag="ul"
              class="flex flex-col"
              @enter="onEnter"
              @after-enter="onAfterEnter"
              @leave="onLeave"
            >
              <li v-for="(events, month) in eventsByMonth" :key="month">
                <Disclosure class="relative flex flex-col mb-[2px]">
                  <Disclosure.Button class="group flex gap-[2px] items-stretch min-h-[50px] w-full aria-expanded:sticky top-0 z-10 aria-expanded:md:static">
                    <div class="flex justify-center items-center w-[30px] border border-gray-400 bg-white transition-colors duration-500 ease-in-out group-aria-expanded:bg-primary group-aria-expanded:text-white">
                      <i class="icon-arrow-button text-10 md:text-16 transition-transform rotate-90 group-aria-expanded:-rotate-90" />
                    </div>
                    <div class="flex flex-1 items-center border border-gray-400 pl-[20px] bg-white transition-colors duration-500 ease-in-out group-aria-expanded:bg-primary group-aria-expanded:text-white">
                      {{ month }}
                    </div>
                  </Disclosure.Button>
                  <Disclosure.Panel #default="{ isOpen }">
                    <Transition.Panel>
                      <div v-if="isOpen">
                        <Disclosure.Group accordion>
                          <TransitionGroup
                            name="panel-group"
                            tag="ul"
                            class="flex flex-col"
                            @enter="onEnter"
                            @after-enter="onAfterEnter"
                            @leave="onLeave"
                          >
                            <li v-for="event in events" :key="event.id">
                              <Disclosure v-if="event.options">
                                <Disclosure.Button as="div" role="button" class="group flex gap-[2px] min-h-[50px] mt-[2px]">
                                  <div class="flex items-center justify-center w-[30px] border border-gray-400">
                                    <i class="icon-arrow-button text-10 md:text-16 transition-transform rotate-90 group-aria-expanded:-rotate-90" />
                                  </div>

                                  <div class="flex flex-col md:flex-row flex-1 items-start md:items-center justify-center md:justify-start border border-gray-400 px-[min(20px,2%)] py-[2px]">
                                    {{ useLocaleDate(event.date) }}
                                    <span class="font-medium italic ml-[2px]">({{ event.day_of_week }})</span>
                                  </div>

                                  <div class="flex flex-1 items-center justify-start border border-gray-400 px-[min(20px,2%)] py-[2px]">
                                    <div>
                                      <div>
                                        <b>{{ event.track }}</b> <span class="whitespace-nowrap text-12 md:text-14">({{ tracks[event.track].length }} m)</span>
                                      </div>
                                      <div class="text-12 md:text-14">
                                        {{ tracks[event.track].address }}
                                      </div>
                                    </div>
                                  </div>

                                  <div class="flex flex-1 items-center justify-start border border-gray-400 px-[min(20px,2%)] py-[2px]">
                                    {{ event.contractor }}
                                  </div>
                                </Disclosure.Button>
                                <Disclosure.Panel #default="{ isOpen: isOptionPanelOpen }">
                                  <Transition.Panel>
                                    <div v-show="isOptionPanelOpen">
                                      <div class="flex flex-col justify-center gap-[5px] min-h-[50px] p-[5px]">
                                        <p v-for="o in event.options.split(' ').filter(Boolean)" :key="o">
                                          {{ getOption(o) }}
                                        </p>
                                      </div>
                                    </div>
                                  </Transition.Panel>
                                </Disclosure.Panel>
                              </Disclosure>
                              <div v-else class="flex gap-[2px] min-h-[50px] mt-[2px]">
                                <div class="w-[30px] border border-gray-400" />
                                <div class="flex flex-col md:flex-row flex-1 items-start md:items-center justify-center md:justify-start border border-gray-400 px-[min(20px,2%)] py-[2px]">
                                  {{ useLocaleDate(event.date) }}
                                  <span class="font-medium italic ml-[2px]"> ({{ event.day_of_week }})</span>
                                </div>

                                <div class="flex flex-1 items-center justify-start border border-gray-400 px-[min(20px,2%)] py-[2px]">
                                  <div>
                                    <div>
                                      <b>{{ event.track }}</b> <span class="whitespace-nowrap text-12 md:text-14">({{ tracks[event.track].length }} m)</span>
                                    </div>
                                    <div class="text-12 md:text-14">
                                      {{ tracks[event.track].address }}
                                    </div>
                                  </div>
                                </div>
                                <div class="flex flex-1 items-center justify-start border border-gray-400 px-[min(20px,2%)] py-[2px]">
                                  {{ event.contractor }}
                                </div>
                              </div>
                            </li>
                          </TransitionGroup>
                        </Disclosure.Group>
                      </div>
                    </Transition.Panel>
                  </Disclosure.Panel>
                </Disclosure>
              </li>
            </TransitionGroup>
          </Disclosure.Group>
        </div>
      </div>
    </div>
    <ClientOnly>
      <Teleport to="body">
        <Transition.Sidebar>
          <Dialog
            v-if="filtersDialogOpen"
            :focus-after-closed="showFiltersButton"
            aria-labelledby="dialog_filter_label"
            class="!p-0"
            @close="filtersDialogOpen = false"
          >
            <Dialog.Overlay closeable class="backdrop-blur bg-secondary/80" />
            <Dialog.Panel class="relative bg-white shadow-[rgb(0_0_0/20%)_0_3px_6px] overflow-hidden flex flex-col flex-start max-h-full w-10/12 h-full">
              <div class="flex items-center border-b border-gray-400 py-[10px] mx-[20px]">
                <h3 id="dialog_filter_label" class="inline-flex items-center gap-[5px] font-medium text-20 flex-1 text-action">
                  <Filter />
                  Filtry
                </h3>
                <Dialog.Close
                  type="button"
                  aria-label="Zamknij"
                  class="text-20 transition-colors duration-500 ease-in-out hover:text-red focus:text-red"
                >
                  <i class="icon-close" />
                </Dialog.Close>
              </div>
              <div class="w-full mb-[20px] p-[20px_20px_0_20px] overflow-auto">
                <Disclosure class="border-b border-gray-400">
                  <Disclosure.Button class="group w-full text-left flex items-center justify-between py-[8px] pr-[4px] text-16 font-medium">
                    Tor <i class="icon-arrow-button text-[15px] transition-transform rotate-90 group-aria-expanded:-rotate-90" />
                  </Disclosure.Button>
                  <Disclosure.Panel #default="{ isOpen }">
                    <Transition.Panel>
                      <div v-show="isOpen">
                        <div class="flex flex-col pb-[15px]">
                          <label v-for="track in tracks" :key="track.name" class="relative inline-flex items-baseline cursor-pointer py-[5px]">
                            <input v-model="filters.track" name="tracks" type="checkbox" :value="track.name" class="shrink-0 w-[14px] h-[14px] accent-action">
                            <span class="ml-[5px]">{{ track.name }}</span>
                          </label>
                        </div>
                      </div>
                    </Transition.Panel>
                  </Disclosure.Panel>
                </Disclosure>

                <Disclosure class="border-b border-gray-400">
                  <Disclosure.Button class="group w-full text-left flex items-center justify-between py-[8px] pr-[4px] text-16 font-medium">
                    Wykonawcy <i class="icon-arrow-button text-[15px] transition-transform rotate-90 group-aria-expanded:-rotate-90" />
                  </Disclosure.Button>
                  <Disclosure.Panel #default="{ isOpen }">
                    <Transition.Panel>
                      <div v-show="isOpen">
                        <div class="flex flex-col pb-[15px]">
                          <label v-for="contractor in contractors" :key="contractor.name" class="relative inline-flex items-baseline cursor-pointer py-[5px]">
                            <input v-model="filters.contractor" name="contractor" type="checkbox" :value="contractor.name" class="shrink-0 w-[14px] h-[14px] accent-action">
                            <span class="ml-[5px]">{{ contractor.name }}</span>
                          </label>
                        </div>
                      </div>
                    </Transition.Panel>
                  </Disclosure.Panel>
                </Disclosure>
              </div>
            </Dialog.Panel>
          </Dialog>
        </Transition.Sidebar>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style lang="scss">
.panel-group {
  &-enter-from {
    @apply h-0 opacity-0;
  }

  &-enter-active {
    @apply overflow-hidden;
    transition: height 300ms, opacity 500ms ease-in-out;
  }

  &-enter-to {
    @apply opacity-100;
  }

  &-leave-active {
    @apply overflow-hidden;
    transition: height 300ms, opacity 200ms ease-in-out;
  }

  &-leave-from {
    @apply opacity-100;
  }

  &-leave-to {
    @apply h-0 opacity-0;
  }
}
</style>
